import { useCallback, useRef, useState } from "react";
import { useVideoStats } from "./useVideoStats";
import { useVideoProgress } from "./useVideoProgress";
import VideoPlayerEventsService from "../services/videoPlayerEvents.service";

interface IPandaEvent {
	message: string;
	video: string;
	currentTime: number;
	isMutedIndicator: boolean;
	videoDuration: number;
}

interface UseVideoPlayerProps {
	lessonId?: number;
	videoUrl?: string;
}

interface UseVideoPlayerReturn {
	playing: boolean;
	startTime: number;
	playerRef: React.RefObject<any>;
	handleStartVideo: (isPanda: boolean, pandaEvent?: IPandaEvent) => Promise<void>;
	handlePauseVideo: (isPanda: boolean, pandaEvent?: IPandaEvent) => Promise<void>;
	handleOnProgressVideo: (isFinished: boolean, isPanda: boolean, pandaEvent?: IPandaEvent, progressData?: any) => Promise<void>;
	pauseVideo: () => void;
	seekTo: (time: number) => void;
	getCurrentTime: () => number;
	getDuration: () => number;
}

/**
 * Main hook that orchestrates video playback
 * Composes useVideoStats and useVideoProgress
 * Handles all video player events (play, pause, progress)
 */
export const useVideoPlayer = ({ lessonId, videoUrl }: UseVideoPlayerProps): UseVideoPlayerReturn => {
	const [playing, setPlaying] = useState(false);
	const playerRef = useRef<any>(null);

	const {
		updateVideoStats,
		resetVideoStats,
		setVisualizationId,
		getVideoStats,
		getVisualizationId,
	} = useVideoStats({ lessonId });

	const getCurrentTime = useCallback((): number => {
		if (!playerRef.current) return 0;

		// Try different methods to get current time (supports multiple player types)
		if (typeof playerRef.current.getCurrentTime === "function") {
			return playerRef.current.getCurrentTime();
		}
		if (playerRef.current.currentTime !== undefined) {
			return playerRef.current.currentTime;
		}
		if (playerRef.current.getInternalPlayer) {
			const internalPlayer = playerRef.current.getInternalPlayer();
			if (internalPlayer && typeof internalPlayer.getCurrentTime === "function") {
				return internalPlayer.getCurrentTime();
			}
		}
		return 0;
	}, []);

	const getDuration = useCallback((): number => {
		if (!playerRef.current) return 0;

		if (typeof playerRef.current.getDuration === "function") {
			return playerRef.current.getDuration();
		}
		if (playerRef.current.duration !== undefined) {
			return playerRef.current.duration;
		}
		if (playerRef.current.getInternalPlayer) {
			const internalPlayer = playerRef.current.getInternalPlayer();
			if (internalPlayer && typeof internalPlayer.getDuration === "function") {
				return internalPlayer.getDuration();
			}
		}
		return 0;
	}, []);

	const { startTime, saveProgress } = useVideoProgress({
		lessonId,
		videoUrl,
		playing,
		getPlayerCurrentTime: getCurrentTime,
	});

	const handleStartVideo = useCallback(
		async (isPanda: boolean, pandaEvent?: IPandaEvent) => {
			let inicio: number;
			let tempoTotal: number;

			setPlaying(true);

			if (isPanda && pandaEvent) {
				inicio = Math.round(pandaEvent.currentTime);
				tempoTotal = Math.round(pandaEvent.videoDuration);
			} else {
				inicio = Math.round(getCurrentTime());
				tempoTotal = Math.round(getDuration());
			}

			const currentStats = getVideoStats();
			const ultTempo = currentStats.tempoAtual;

			updateVideoStats({
				isPaused: false,
				tempoAtual: inicio,
				ultimoTempoConhecido: ultTempo === inicio ? null : ultTempo,
			});

			const videoPlayerEventsService = new VideoPlayerEventsService();
			const currentVisualizationId = getVisualizationId();

			if (!currentVisualizationId) {
				const response = await videoPlayerEventsService.iniciarAssistir(
					lessonId,
					inicio,
					tempoTotal,
					getVideoStats()
				);
				setVisualizationId(response.id);
			} else {
				const response = await videoPlayerEventsService.iniciarAssistir(
					lessonId,
					inicio,
					tempoTotal,
					getVideoStats(),
					currentVisualizationId
				);
				if (response.id !== currentVisualizationId) {
					setVisualizationId(response.id);
				}
			}
		},
		[lessonId, getCurrentTime, getDuration, getVideoStats, getVisualizationId, updateVideoStats, setVisualizationId]
	);

	const handlePauseVideo = useCallback(
		async (isPanda: boolean, pandaEvent?: IPandaEvent) => {
			setPlaying(false);

			const currentVisualizationId = getVisualizationId();
			if (!currentVisualizationId) return;

			let tempoPause: number;
			let tempoTotal: number;

			if (isPanda && pandaEvent) {
				tempoPause = Math.round(pandaEvent.currentTime);
				tempoTotal = Math.round(pandaEvent.videoDuration);
			} else {
				tempoPause = Math.round(getCurrentTime());
				tempoTotal = Math.round(getDuration());
			}

			updateVideoStats({
				isPaused: true,
				tempoPause,
				tempoAtual: tempoPause,
				ultimoTempoConhecido: tempoPause,
			});

			const videoPlayerEventsService = new VideoPlayerEventsService();
			await videoPlayerEventsService.pausarVideo(
				currentVisualizationId,
				tempoPause,
				tempoTotal,
				getVideoStats()
			);
		},
		[getCurrentTime, getDuration, getVideoStats, getVisualizationId, updateVideoStats]
	);

	const handleOnProgressVideo = useCallback(
		async (isFinished: boolean, isPanda: boolean, pandaEvent?: IPandaEvent, progressData?: any) => {
			const currentVisualizationId = getVisualizationId();
			if (!currentVisualizationId) return;

			const currentStats = getVideoStats();
			if (currentStats.isPaused && !isFinished) return;

			if (isFinished) {
				setPlaying(false);
			}

			let tempoAtual: number;

			if (isPanda && pandaEvent) {
				tempoAtual = Math.round(pandaEvent.currentTime);
			} else if (progressData) {
				tempoAtual = Math.round(progressData.playedSeconds || 0);
			} else {
				tempoAtual = Math.round(getCurrentTime());
			}

			updateVideoStats({
				isPaused: isFinished,
				tempoAtual,
				ultimoTempoConhecido: tempoAtual,
			});

			// Save progress to localStorage
			if (lessonId && tempoAtual > 0) {
				saveProgress(tempoAtual);
			}

			const videoPlayerEventsService = new VideoPlayerEventsService();
			await videoPlayerEventsService.assistirVideo(
				currentVisualizationId,
				tempoAtual,
				isFinished,
				getVideoStats()
			);
		},
		[lessonId, getCurrentTime, getVideoStats, getVisualizationId, updateVideoStats, saveProgress]
	);

	const pauseVideo = useCallback(() => {
		setPlaying(false);
	}, []);

	const seekTo = useCallback((time: number) => {
		if (!playerRef.current) return;

		// Try the api property first (works for YouTube)
		if (playerRef.current.api?.seekTo) {
			playerRef.current.api.seekTo(time);
			return;
		}

		// Fallback methods
		if (typeof playerRef.current.seekTo === "function") {
			playerRef.current.seekTo(time);
		} else if (playerRef.current.getInternalPlayer) {
			const internalPlayer = playerRef.current.getInternalPlayer();
			if (internalPlayer && typeof internalPlayer.seekTo === "function") {
				internalPlayer.seekTo(time, true);
			}
		}
	}, []);

	return {
		playing,
		startTime,
		playerRef,
		handleStartVideo,
		handlePauseVideo,
		handleOnProgressVideo,
		pauseVideo,
		seekTo,
		getCurrentTime,
		getDuration,
	};
};

export type { UseVideoPlayerReturn, IPandaEvent };
